import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import AppShell from "./components/AppShell";
import { useAuth } from "./hooks/useAuth";
import LoadingSpinner from "./components/LoadingSpinner";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PackagesPage = lazy(() => import("./pages/PackagesPage"));
const PackageDetailPage = lazy(() => import("./pages/PackageDetailPage"));
const ShipmentsPage = lazy(() => import("./pages/ShipmentsPage"));
const StoresPage = lazy(() => import("./pages/StoresPage"));
const AddressPage = lazy(() => import("./pages/AddressPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PricingCalculatorPage = lazy(() => import("./pages/PricingCalculatorPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminSettingsPage = lazy(() => import("./pages/AdminSettingsPage"));
const AdminPricingPage = lazy(() => import("./pages/AdminPricingPage"));
const AdminCustomerDetailPage = lazy(() => import("./pages/AdminCustomerDetailPage"));
const AdminReportsPage = lazy(() => import("./pages/AdminReportsPage"));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="flex h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div className="flex h-full items-center justify-center"><LoadingSpinner size="lg" /></div>}>{children}</Suspense>;
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <SuspenseWrap>{children}</SuspenseWrap>;
}

export const router = createBrowserRouter([
  { path: "/", element: <SuspenseWrap><LandingPage /></SuspenseWrap> },
  { path: "/login", element: <SuspenseWrap><LoginPage /></SuspenseWrap> },
  { path: "/register", element: <SuspenseWrap><RegisterPage /></SuspenseWrap> },
  {
    path: "/dashboard",
    element: <ProtectedRoute><AppShell><Lazy><DashboardPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/packages",
    element: <ProtectedRoute><AppShell><Lazy><PackagesPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/packages/:packageId",
    element: <ProtectedRoute><AppShell><Lazy><PackageDetailPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/shipments",
    element: <ProtectedRoute><AppShell><Lazy><ShipmentsPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/stores",
    element: <ProtectedRoute><AppShell><Lazy><StoresPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/address",
    element: <ProtectedRoute><AppShell><Lazy><AddressPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/profile",
    element: <ProtectedRoute><AppShell><Lazy><ProfilePage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/calculator",
    element: <ProtectedRoute><AppShell><Lazy><PricingCalculatorPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/invoices",
    element: <ProtectedRoute><AppShell><Lazy><InvoicesPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/notifications",
    element: <ProtectedRoute><AppShell><Lazy><NotificationsPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/admin",
    element: <ProtectedRoute><AppShell admin><Lazy><AdminDashboardPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/admin/settings",
    element: <ProtectedRoute><AppShell admin><Lazy><AdminSettingsPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/admin/pricing",
    element: <ProtectedRoute><AppShell admin><Lazy><AdminPricingPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/admin/reports",
    element: <ProtectedRoute><AppShell admin><Lazy><AdminReportsPage /></Lazy></AppShell></ProtectedRoute>,
  },
  {
    path: "/admin/customers/:customerId",
    element: <ProtectedRoute><AppShell admin><Lazy><AdminCustomerDetailPage /></Lazy></AppShell></ProtectedRoute>,
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);